import { Link, useNavigate } from "@tanstack/react-router";
import { Home, ArrowLeft } from "lucide-react";
import * as React from "react";

import { Button } from "@/shared/ui/button";

type Props = {
  title: "Error" | "400" | "401" | "404" | "403" | "500";
  message?: React.ReactNode;
  children?: React.ReactNode;
  withLayout?: boolean;
};

const ERROR_TITLES: Record<
  Props["title"],
  { heading: string; description: string }
> = {
  "400": {
    heading: "Bad Request",
    description:
      "The request could not be understood or was missing required parameters.",
  },
  "401": {
    heading: "Unauthorized",
    description:
      "You need to be authenticated to access this resource.",
  },
  "403": {
    heading: "Forbidden",
    description:
      "You don't have permission to access this resource.",
  },
  "404": {
    heading: "Page Not Found",
    description:
      "The page you are looking for might have been removed or is temporarily unavailable.",
  },
  "500": {
    heading: "Server Error",
    description:
      "An unexpected error occurred on the server. Please try again later.",
  },
  Error: {
    heading: "Something went wrong",
    description:
      "An unexpected error occurred. Please try again later.",
  },
};

export function GeneralError({
  title,
  message,
  children,
  withLayout = false,
}: Props) {
  const navigate = useNavigate();
  const errorInfo = ERROR_TITLES[title];
  const is404 = title === "404";

  const content = (
    <div className="container mx-auto max-w-4xl px-4 py-16 md:px-6">
      <div className="space-y-8 text-center">
        <div className="space-y-4">
          <h1 className="text-9xl font-bold text-warning-foreground">
            {title}
          </h1>
          <h2 className="text-3xl font-semibold">
            {errorInfo.heading}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            {message || children || errorInfo.description}
          </p>
        </div>

        <div className="flex flex-col items-center justify-center gap-4 pt-8 sm:flex-row">
          {is404 ? (
            <Link to="/">
              <Button
                size="lg"
                spacing="wide"
                className="min-w-50"
              >
                <Home className="h-5 w-5" />
                Back to Homepage
              </Button>
            </Link>
          ) : (
            <>
              <Button
                size="lg"
                spacing="wide"
                className="min-w-50"
                onClick={() => navigate({ to: ".." })}
              >
                <ArrowLeft className="h-5 w-5" />
                Go Back
              </Button>
              <Link to="/">
                <Button
                  variant="outline"
                  size="lg"
                  spacing="wide"
                  className="min-w-50"
                >
                  <Home className="h-5 w-5" />
                  Back to Homepage
                </Button>
              </Link>
            </>
          )}
        </div>

        <div className="mt-12 border-t pt-12">
          <h3 className="mb-6 text-xl font-semibold">
            Popular Pages
          </h3>
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
            <Link
              to="/app/dashboard"
              className="rounded-lg border p-4 transition-all hover:border-warning-border"
            >
              <p className="font-medium">Dashboard</p>
              <p className="text-sm text-muted-foreground">
                Go to your dashboard
              </p>
            </Link>
            <Link
              to="/contact"
              className="rounded-lg border p-4 transition-all hover:border-warning-border"
            >
              <p className="font-medium">Contact</p>
              <p className="text-sm text-muted-foreground">
                Get in touch
              </p>
            </Link>
          </div>
        </div>

        <div className="mt-8 pt-8">
          <div className="text-sm text-muted-foreground">
            <p className="mb-2 font-semibold">Need help?</p>
            <p>
              If you think this is a mistake, please{" "}
              <Link
                to="/contact"
                className="text-warning-foreground underline hover:text-warning-foreground"
              >
                contact support
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (withLayout) {
    return content;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      {content}
    </div>
  );
}
